"use client";

import {
  FC,
  memo,
  useEffect,
  useState,
  Suspense,
} from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

import { useSignupFlowReset } from "apps/website/hooks/useSignupFlowReset";
import DefaultHead from "apps/website/components/page/DefaultHead/DefaultHead";
import { makePageTags } from "apps/website/utils/website/head";
import Header from "apps/website/components/page/Header/Header";
import {
  HEADER_LINKS_NONE,
} from "apps/website/components/page/Header/Header.map";
import {
  SIGNUP_FLOW_PAGES,
  SignupFlowSlug,
} from "libs/state/src/lib/stores/signupFlow/constants";
import { IDiscount } from "libs/state/src/lib/stores/signupFlow/types";
import { useFlowNavigation, useSignupFlowStore } from "@./state";
import { getInterpolationVariables } from "libs/utils/sign-up/fresh";

import { formatDiscount } from "../components/feature/PricingTabPanel/utils";
import Main from "../components/layout/Main/Main";
import Footer from "../components/page/Footer/Footer";
import { themeRootClassMap } from "../maps/Theme.map";
import cx from "../utils/misc/cx";
import {
  QueryParamHandler,
} from "../components/sign-up/fresh/common/QueryParamHandler/QueryParamHandler";
import {
  SignupLoadingScreen,
} from "../components/sign-up/fresh/common/SignupLoadingScreen/SignupLoadingScreen";
import {
  ProgressSteps,
} from "../components/sign-up/fresh/common/ProgressSteps/ProgressSteps";
import {
  TitleSection,
} from "../components/sign-up/fresh/common/TitleSection/TitleSection";
import SignupPromotionalBanner
  from "../components/sign-up/fresh/common/SignupPromotionalBanner/SignupPromotionalBanner";

import { ILayout } from "./layouts.map";

export interface SignupFlowLayoutProps extends Partial<ILayout> {}

const INITIAL_SLUG = SignupFlowSlug.CAT_NAMES;
const LOGIN_LINK = { id: "g-0", name: "Login", to: "/login" };

const checkShouldRedirect = (
  isStoreInitialised: boolean,
  pathname: string,
  versionMismatchDetected: boolean,
  activeCatIdsLength: number,
): boolean => isStoreInitialised &&
  pathname !== INITIAL_SLUG &&
  (versionMismatchDetected || activeCatIdsLength === 0);

const getAnnouncementText = (
  discount: IDiscount,
  bannerT: ReturnType<typeof useTranslations>,
): string => {
  const formattedDiscount = formatDiscount(discount);
  return discount.type === "absolute_total"
    ? bannerT("announcementAbsolute", { discount: formattedDiscount })
    : bannerT("announcementOff", { discount: formattedDiscount });
};

const FreshSignupFlow: FC<Readonly<SignupFlowLayoutProps>> =
({ children, head, lastBuilt }) => {
  const t = useTranslations("SUFv2.titles");
  const bannerT = useTranslations("SUFv2.PromotionalBanner");
  const uxT = useTranslations("General.UX");
  const pathname = usePathname();
  const [ hideBackButton, setHideBackButton ] = useState(true);
  const [ mounted, setMounted ] = useState(false);

  const { canGoBack, goBack } = useFlowNavigation(pathname);

  const {
    cats,
    activeCatId,
    isStoreInitialised,
    versionMismatchDetected,
    getActiveCatIds,
    discount,
    isTransitionVisible,
  } = useSignupFlowStore((state) => ({
    activeCatId: state.activeCatId,
    cats: state.cats,
    isStoreInitialised: state.isStoreInitialised,
    versionMismatchDetected: state.versionMismatchDetected,
    getActiveCatIds: state.getActiveCatIds,
    discount: state.subscription.discount,
    isTransitionVisible: state.viewState.catParentDetails?.isTransitionVisible ?? false,
  }));

  // Synchronously check if we should redirect (prevents rendering with invalid state)
  const shouldRedirect = checkShouldRedirect(
    isStoreInitialised,
    pathname,
    versionMismatchDetected,
    getActiveCatIds().length,
  );

  const currentCat = cats[activeCatId];

  const {
    title,
    hideProgressSteps,
    hideFooter = true,
    bgColor = "light-grey",
    headerBgColor = "light",
    hidePromoBanner = false,
    promoBannerAnimateOnEntry = false,
  } = SIGNUP_FLOW_PAGES[pathname as SignupFlowSlug];

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setHideBackButton(pathname === INITIAL_SLUG);
  }, [ pathname ]);

  useSignupFlowReset();

  const getTitle = () => {
    if (title && currentCat) {
      const variables = getInterpolationVariables({ name: currentCat?.name, gender: currentCat?.gender });
      return t(title, variables);
    }
    return t(title) || "";
  };

  const showPromoBanner = !hidePromoBanner && !isTransitionVisible;

  return (
    <div>
      <DefaultHead
        seo={makePageTags(head?.seo)}
        /* @ts-ignore */
        hideFromSearch={head?.hide_from_search_engines}
        lastBuilt={lastBuilt}
        fallbackTags={head?.fallbackTags && makePageTags(head.fallbackTags)}
      />
      <Suspense>
        <QueryParamHandler />
      </Suspense>

      <Header
        {...HEADER_LINKS_NONE}
        hasAnnouncementBanner={false}
        guestLinks={hideBackButton ? [ LOGIN_LINK ] : []}
        historyNavigation={{
          back: () => (canGoBack ? goBack() : undefined),
          hide: hideBackButton,
        }}
        backgroundColor={headerBgColor}
      />

      <Main className={cx(themeRootClassMap[bgColor], "min-h-[calc(100dvh-var(--header-height-mobile))] xl:min-h-[calc(100dvh-var(--header-height-desktop))]")}>
        { !mounted || !isStoreInitialised || shouldRedirect ? (
          <SignupLoadingScreen uxT={uxT} />
        ) : (
          <>
            <div className="fixed top-[var(--header-height-mobile)] xl:top-[var(--header-height-desktop)] w-full z-20 bg-white">
              { showPromoBanner && (
                <SignupPromotionalBanner
                  announcements={[
                    {
                      text: getAnnouncementText(discount, bannerT),
                      showTag: true,
                    },
                  ]}
                  theme="mid-green"
                  animateOnEntry={promoBannerAnimateOnEntry}
                />
              ) }
              <ProgressSteps
                hideProgressSteps={hideProgressSteps}
                pathname={pathname as SignupFlowSlug}
              />
            </div>
            { showPromoBanner && (
              <div className="h-[44px] xl:h-[60px] w-full" />
            ) }
            <TitleSection
              title={title}
              hideProgressSteps={hideProgressSteps}
              getTitle={getTitle}
            />
            { children }
          </>
        ) }
      </Main>

      { !hideFooter && (
        <Footer />
      ) }
    </div>
  );
};

export default memo(FreshSignupFlow);
